import React, { useState } from "react";
import { Modal, Form, Input, Row, Col, Select, Space, message } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { UiButton, ActionIconButton } from "../../ui";
import { useOrgFacilities } from "../common/useOrgFacilities";
import { addPharmacyUserApi } from "../../../services/auth.services";
import { getErrorMessage } from "../../../helpers/error";
import { isValidEmail } from "../../../helpers/validationPatterns";


const dedupeByFacility = (roles) => {
  const seen = new Set();
  return (roles || []).filter((role) => {
    const id = role?.facility;
    if (id === undefined || id === null || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
};

const PharmacyUserForm = ({ open, setOpen, onSaved }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const { destinations, currentFacilityId, currentFacilityName } =
    useOrgFacilities();

  // Every facility in the org is assignable, including the one being worked in.
  const facilityOptions = [
    ...(currentFacilityId
      ? [{ value: currentFacilityId, label: currentFacilityName }]
      : []),
    ...destinations,
  ];

 
  const rows = Form.useWatch("roles", form) || [];

  const optionsFor = (index) => {
    const takenElsewhere = rows
      .filter((_, i) => i !== index)
      .map((row) => row?.facility)
      .filter((value) => value !== undefined && value !== null);

    /* Shown and struck through rather than dropped: a facility that quietly
       disappears reads as missing, one that is greyed out reads as done. */
    return facilityOptions.map((option) =>
      takenElsewhere.includes(option.value)
        ? { ...option, disabled: true, label: `${option.label} · already added` }
        : option
    );
  };

  const chosenCount = rows.filter(
    (row) => row?.facility !== undefined && row?.facility !== null
  ).length;
  const allFacilitiesUsed =
    facilityOptions.length > 0 && chosenCount >= facilityOptions.length;

  const close = () => {
    setOpen(false);
    form.resetFields();
  };

  const handleSubmit = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    setSaving(true);
    addPharmacyUserApi({
      mobile: values.mobile,
      email: values.email,
      first_name: values.first_name || undefined,
      last_name: values.last_name || undefined,
      roles: dedupeByFacility(values.roles).map((role) => ({
        role: "Pharmacist",
        facility: role.facility,
      })),
    })
      .then((res) => {
        message.success(res?.data?.message || "User added successfully");
        onSaved?.();
        close();
      })
      .catch((err) => message.error(getErrorMessage(err)))
      .finally(() => setSaving(false));
  };

  return (
    <Modal
      open={open}
      onCancel={close}
      title="Add Pharmacy User"
      width={720}
      destroyOnClose
      footer={
        <Space>
          <UiButton variant="secondary" onClick={close}>
            Cancel
          </UiButton>
          <UiButton loading={saving} onClick={handleSubmit}>
            Add User
          </UiButton>
        </Space>
      }
    >
      <Form
        layout="vertical"
        form={form}
        initialValues={{ roles: [{}] }}
      >
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              label="Mobile"
              name="mobile"
              rules={[
                { required: true, message: "Please enter mobile number" },
                {
                  pattern: /^\d{10}$/,
                  message: "Mobile must be exactly 10 digits",
                },
              ]}
            >
              <Input placeholder="10-digit mobile" maxLength={10} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="Email"
              name="email"
              rules={[
                { required: true, message: "Please enter email" },
                {
                  validator: (_, value) =>
                    !value || isValidEmail(value)
                      ? Promise.resolve()
                      : Promise.reject(new Error("Please enter a valid email")),
                },
              ]}
            >
              <Input placeholder="name@example.com" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="First Name" name="first_name">
              <Input placeholder="First name" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Last Name" name="last_name">
              <Input placeholder="Last name" />
            </Form.Item>
          </Col>
        </Row>

        <Form.List name="roles">
          {(fields, { add, remove }) => (
            <>
              {fields.map((field, index) => (
                <Row gutter={16} key={field.key} align="middle">
                  <Col xs={24} md={20}>
                    <Form.Item
                      {...field}
                      key={`${field.key}-facility`}
                      name={[field.name, "facility"]}
                      label={index === 0 ? "Facility" : ""}
                      rules={[
                        { required: true, message: "Please select facility" },
                      ]}
                    >
                      <Select
                        showSearch
                        optionFilterProp="label"
                        placeholder="Select facility"
                        options={optionsFor(index)}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={8} md={4}>
                    <Form.Item label={index === 0 ? " " : ""}>
                      <ActionIconButton
                        aria-label="Remove facility"
                        disabled={fields.length === 1}
                        onClick={() => remove(field.name)}
                      >
                        <DeleteOutlined />
                      </ActionIconButton>
                    </Form.Item>
                  </Col>
                </Row>
              ))}

              <UiButton
                variant="ghost"
                icon={<PlusOutlined />}
                disabled={allFacilitiesUsed}
                title={
                  allFacilitiesUsed
                    ? "This user is already assigned to every facility"
                    : undefined
                }
                onClick={() => add({})}
              >
                Add Facility
              </UiButton>
            </>
          )}
        </Form.List>
      </Form>
    </Modal>
  );
};

export default PharmacyUserForm;
